import {
  TerminalServerMessage,
  TerminalSession,
  type TerminalAuth,
  type TerminalClientMessage,
} from "@cloudserver/contracts";
import type { SearchAddon } from "@xterm/addon-search";
import type { Unicode11Addon } from "@xterm/addon-unicode11";
import type { WebglAddon } from "@xterm/addon-webgl";
import type { WebLinksAddon } from "@xterm/addon-web-links";
import type { ITheme, Terminal } from "@xterm/xterm";
import { api, ApiProblem } from "../api/client.js";
import { disposeVnc } from "../pages/server/console/connection.js";
import { disposeRdp } from "../rdp/connection.js";
import { fontStack, loadFont } from "./fonts.js";
import { paneGeometry, type Size } from "./geometry.js";
import { takeTerminalText, withoutEnter } from "./prefill.js";
import { terminalSettings, type TerminalSettings } from "./settings.js";
import { RESTORED, rememberUser, terminals } from "./store.js";
import { onThemeChange, terminalTheme } from "./theme.js";
import { withModifiers, type StickyModifier } from "./touch-keys.js";

/** A font that has not loaded by then is not waited for: the fallback draws, it swaps in later. */
const FONT_WAIT_MS = 3000;
/** A handed-over command waits this long after the shell is ready, so it lands after the prompt. */
const PREFILL_DELAY_MS = 500;

const fontReady = (id: string) =>
  Promise.race([loadFont(id), new Promise<void>((done) => setTimeout(done, FONT_WAIT_MS))]);

/*
 * One xterm and one WebSocket per tab, outside React: they keep running
 * while the person moves around the panel. The xterm is drawn into its own
 * element, which the workspace moves into a pane and back to a hidden
 * parking spot. xterm and its add-ons load on the first terminal only.
 */

const socketUrl = (path: string) =>
  `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}${path}`;

let modules: Promise<Modules> | null = null;
interface Modules {
  Terminal: typeof Terminal;
  SearchAddon: typeof SearchAddon;
  WebLinksAddon: typeof WebLinksAddon;
  Unicode11Addon: typeof Unicode11Addon;
  WebglAddon: typeof WebglAddon;
}

function loadXterm(): Promise<Modules> {
  modules ??= Promise.all([
    import("@xterm/xterm"),
    import("@xterm/addon-search"),
    import("@xterm/addon-web-links"),
    import("@xterm/addon-unicode11"),
    import("@xterm/addon-webgl"),
    import("@xterm/xterm/css/xterm.css"),
  ]).then(([xterm, search, links, unicode, webgl]) => ({
    Terminal: xterm.Terminal,
    SearchAddon: search.SearchAddon,
    WebLinksAddon: links.WebLinksAddon,
    Unicode11Addon: unicode.Unicode11Addon,
    WebglAddon: webgl.WebglAddon,
  }));
  return modules;
}

/** Where terminals wait while no pane shows them: laid out (xterm measures), but invisible. */
function parking(): HTMLElement {
  let element = document.getElementById("terminal-parking");
  if (!element) {
    element = document.createElement("div");
    element.id = "terminal-parking";
    element.setAttribute("aria-hidden", "true");
    element.inert = true;
    Object.assign(element.style, {
      position: "fixed",
      left: "-10000px",
      top: "0",
      width: "960px",
      height: "540px",
      visibility: "hidden",
      overflow: "hidden",
    });
    document.body.append(element);
  }
  return element;
}

export interface ConnectOptions {
  /** Password mode: sent once in the start message, kept nowhere. */
  password?: string;
  /**
   * After a reload: back to the kept shell with this one-time token; when it
   * is no longer kept, a new session (or the password asked, in password mode).
   */
  resumeToken?: string;
  /** A changed host key the person accepted. */
  acceptHostKey?: string;
  /** Switch how to sign in (e.g. to the password after the CA was not trusted). */
  auth?: TerminalAuth;
  unixUser?: string;
}

const encoder = new TextEncoder();

/**
 * Closes a socket the person is done with: `end` first, so the API closes
 * the shell at once instead of keeping it for a resume (only a page that
 * goes away leaves it kept).
 */
function endSocket(socket: WebSocket | null): void {
  if (!socket) return;
  if (socket.readyState === WebSocket.OPEN)
    socket.send(JSON.stringify({ type: "end" } satisfies TerminalClientMessage));
  socket.close();
}

const selectionListeners = new Set<() => void>();

/** Calls `listener` whenever the selection of any terminal changes (the assistant's offer). */
export function onTerminalSelection(listener: () => void): () => void {
  selectionListeners.add(listener);
  return () => selectionListeners.delete(listener);
}

/** What of xterm's private core the fit reads (6.0.0), exactly where its own fit addon reads it. */
interface XtermCore {
  _renderService?: {
    dimensions?: { css?: { cell?: Size } };
    clear?: () => void;
  };
}

const coreOf = (term: Terminal): XtermCore | undefined =>
  (term as unknown as { _core?: XtermCore })._core;

/** The size of one cell in CSS pixels; null until xterm has measured its font. */
function cellOf(term: Terminal): Size | null {
  let cell: Size | undefined;
  try {
    cell = coreOf(term)?._renderService?.dimensions?.css?.cell;
  } catch {
    // xterm's getter throws while it has no renderer (disposed, or between renderers).
    return null;
  }
  return cell && cell.width > 0 && cell.height > 0 ? cell : null;
}

export class TerminalConnection {
  /** The element xterm draws into; moved between panes and the parking spot. */
  readonly element = document.createElement("div");
  private term: Terminal | null = null;
  private searchAddon: SearchAddon | null = null;
  private socket: WebSocket | null = null;
  private readonly ready: Promise<void>;
  private stopTheme: (() => void) | null = null;
  /** What the xterm draws with now, and what was asked for last (a font may still load). */
  private applied: TerminalSettings | null = null;
  private wanted: TerminalSettings = terminalSettings.snapshot();
  private disposed = false;
  /**
   * The touch key row's sticky Ctrl and Alt: the next input goes with them
   * (docs/terminal.md, "Touch keys").
   */
  private held: Record<StickyModifier, boolean> = { ctrl: false, alt: false };
  private readonly modifierListeners = new Set<() => void>();

  constructor(readonly id: string) {
    this.element.className = "h-full w-full";
    this.element.dataset.terminal = id;
    parking().append(this.element);
    this.ready = this.create();
  }

  private async create(): Promise<void> {
    const initial = terminalSettings.snapshot();
    // xterm measures its cell once: the font must be there before it opens.
    const [m] = await Promise.all([loadXterm(), fontReady(initial.font)]);
    if (this.disposed) return;
    const term = new m.Terminal({
      allowProposedApi: true,
      cursorBlink: true,
      fontFamily: fontStack(initial.font),
      fontSize: initial.fontSize,
      scrollback: 5000,
      theme: terminalTheme(initial.theme),
    });
    this.drawn(initial);
    this.paint(term.options.theme);
    this.searchAddon = new m.SearchAddon();
    term.loadAddon(this.searchAddon);
    term.loadAddon(new m.WebLinksAddon());
    term.loadAddon(new m.Unicode11Addon());
    term.unicode.activeVersion = "11";
    term.open(this.element);
    try {
      const webgl = new m.WebglAddon();
      webgl.onContextLoss(() => webgl.dispose());
      term.loadAddon(webgl);
    } catch {
      // No WebGL (old devices, some VMs): xterm keeps drawing with the DOM.
    }
    term.onData((data) => {
      const held = this.held;
      this.releaseModifiers();
      this.send(encoder.encode(withModifiers(data, held)));
    });
    term.onBinary((data) => this.send(Uint8Array.from(data, (c) => c.charCodeAt(0))));
    term.onResize(({ cols, rows }) => this.control({ type: "resize", cols, rows }));
    // Copy on select, as terminals on Linux do; refused clipboard access just means no copy.
    term.onSelectionChange(() => {
      const text = term.getSelection();
      if (text) void navigator.clipboard?.writeText(text).catch(() => undefined);
      for (const listener of selectionListeners) listener();
    });
    // «Как в панели» follows the panel switching between light and dark.
    this.stopTheme = onThemeChange(() => {
      term.options.theme = terminalTheme(terminalSettings.snapshot().theme);
      this.paint(term.options.theme);
    });
    this.term = term;
    this.fit();
    // Settings changed while xterm and the font loaded.
    void this.apply(terminalSettings.snapshot());
  }

  /** Opens a new server session (a new certificate) and joins this terminal to it. */
  async connect(options: ConnectOptions = {}): Promise<void> {
    await this.ready;
    const tab = terminals.tab(this.id);
    if (!tab || this.disposed) return;
    // The old connection's close must not mark the new attempt as disconnected.
    const previous = this.socket;
    this.socket = null;
    endSocket(previous);
    const auth = options.auth ?? tab.auth;
    const unixUser = options.unixUser ?? tab.unixUser;
    terminals.update(this.id, {
      status: "connecting",
      failure: null,
      offeredHostKey: null,
      problem: null,
      auth,
      unixUser,
      // A new session starts without the assistant: the person switches it on again.
      pair: null,
    });
    let session: TerminalSession;
    try {
      session = await api(`/projects/${tab.projectId}/servers/${tab.serverId}/terminal`, {
        method: "POST",
        body: {
          unixUser,
          auth,
          ...(options.acceptHostKey ? { acceptHostKey: options.acceptHostKey } : {}),
          ...(options.resumeToken ? { resumeToken: options.resumeToken } : {}),
        },
        schema: TerminalSession,
      });
    } catch (error) {
      // The kept shell is gone (after the grace period): a new session, or its password asked.
      if (options.resumeToken && error instanceof ApiProblem && error.status === 409) {
        terminals.update(this.id, { resumeToken: null });
        if (auth === "certificate") return this.connect({ unixUser, auth });
        terminals.update(this.id, { status: "disconnected", problem: RESTORED });
        return;
      }
      // The API refused the session (server off, too many terminals, no access…).
      terminals.update(this.id, {
        status: "disconnected",
        failure: null,
        problem:
          error instanceof ApiProblem && error.problem
            ? (error.problem.reason ?? error.problem.code)
            : "unexpected",
      });
      return;
    }
    // The tab was closed while the session was being opened.
    if (this.disposed) return;
    rememberUser(tab.serverId, unixUser);
    terminals.update(this.id, { sessionId: session.sessionId });
    const socket = new WebSocket(socketUrl(session.url));
    socket.binaryType = "arraybuffer";
    this.socket = socket;
    socket.addEventListener("open", () => {
      const term = this.term;
      this.control({
        type: "start",
        cols: term?.cols ?? 80,
        rows: term?.rows ?? 24,
        ...(options.password ? { password: options.password } : {}),
      });
    });
    socket.addEventListener("message", (event: MessageEvent<ArrayBuffer | string>) => {
      if (typeof event.data !== "string") {
        this.term?.write(new Uint8Array(event.data));
        terminals.output(this.id);
        return;
      }
      const parsed = TerminalServerMessage.safeParse(safeJson(event.data));
      if (!parsed.success) return;
      const message = parsed.data;
      if (message.type === "pair") {
        // The assistant's pair mode changed (on, off with why, or what it runs now).
        terminals.update(this.id, {
          pair: {
            on: message.on,
            level: message.level,
            command: message.command,
            reason: message.reason,
          },
        });
        return;
      }
      if (message.type === "ready") {
        terminals.update(this.id, {
          status: "connected",
          hostKeyFingerprint: message.hostKeyFingerprint,
          resumeToken: message.resumeToken ?? null,
        });
        this.term?.focus();
        const serverId = terminals.tab(this.id)?.serverId;
        const prefill = serverId ? takeTerminalText(serverId) : null;
        // After the prompt is drawn; pasted, never sent with Enter: the person runs it.
        if (prefill) setTimeout(() => this.term?.paste(withoutEnter(prefill)), PREFILL_DELAY_MS);
      } else {
        terminals.update(this.id, {
          status: "disconnected",
          failure: message.reason,
          offeredHostKey: message.hostKeyFingerprint ?? null,
        });
      }
    });
    socket.addEventListener("close", () => {
      if (this.socket !== socket) return;
      this.socket = null;
      const current = terminals.tab(this.id);
      if (current && current.status !== "disconnected") {
        terminals.update(this.id, {
          status: "disconnected",
          failure: current.status === "connected" ? "closed" : (current.failure ?? "unreachable"),
        });
      }
    });
  }

  /** Ends the session and keeps the tab («Отключиться»); reconnecting signs in anew. */
  disconnect(): void {
    const socket = this.socket;
    if (!socket) return;
    this.socket = null;
    endSocket(socket);
    terminals.update(this.id, { status: "disconnected", failure: "closed" });
  }

  private send(bytes: Uint8Array<ArrayBuffer>): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(bytes);
  }

  private control(message: TerminalClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }

  /** Types text into the shell (on-screen keys, "send Ctrl+C", paste). */
  input(text: string): void {
    this.send(encoder.encode(text));
  }

  paste(text: string): void {
    this.term?.paste(text);
  }

  /** Ctrl of the touch key row is held: the next key, typed or tapped, goes with Ctrl. */
  get ctrl(): boolean {
    return this.held.ctrl;
  }

  /** Alt of the touch key row is held: the next key, typed or tapped, goes with Alt. */
  get alt(): boolean {
    return this.held.alt;
  }

  /** Latches or lets go one sticky modifier of the touch key row. */
  hold(modifier: StickyModifier, held: boolean): void {
    if (this.held[modifier] === held) return;
    this.held = { ...this.held, [modifier]: held };
    for (const listener of this.modifierListeners) listener();
  }

  /** Lets both sticky modifiers go (after a key, or when the terminal loses the row). */
  releaseModifiers(): void {
    this.hold("ctrl", false);
    this.hold("alt", false);
  }

  onModifiersChange(listener: () => void): () => void {
    this.modifierListeners.add(listener);
    return () => this.modifierListeners.delete(listener);
  }

  /** The shell switched the arrows to application mode (DECCKM: vim, less, htop). */
  get applicationCursor(): boolean {
    return this.term?.modes.applicationCursorKeysMode ?? false;
  }

  /** A key of the touch row: sent like typing (modifiers let go), the view back at the prompt. */
  key(bytes: string): void {
    this.releaseModifiers();
    if (bytes) this.input(bytes);
    this.term?.scrollToBottom();
  }

  focus(): void {
    this.term?.focus();
  }

  /** Takes the focus out of the terminal: on a phone the system keyboard goes away. */
  blur(): void {
    this.term?.blur();
  }

  copySelection(): string {
    return this.term?.getSelection() ?? "";
  }

  find(text: string, direction: "next" | "previous"): boolean {
    if (!this.searchAddon || !text) return false;
    return direction === "next"
      ? this.searchAddon.findNext(text)
      : this.searchAddon.findPrevious(text);
  }

  clearSearch(): void {
    this.searchAddon?.clearDecorations();
  }

  /** Applies changed settings to the open terminal (font after it has loaded). */
  async apply(settings: TerminalSettings): Promise<void> {
    const term = this.term;
    const shown = this.applied;
    if (!term || !shown) return;
    this.wanted = settings;
    if (settings.theme !== shown.theme) {
      term.options.theme = terminalTheme(settings.theme);
      this.paint(term.options.theme);
    }
    if (settings.fontSize !== shown.fontSize) term.options.fontSize = settings.fontSize;
    this.drawn({ ...shown, theme: settings.theme, fontSize: settings.fontSize });
    if (settings.font !== shown.font) {
      await fontReady(settings.font);
      // Another font may have been chosen while this one loaded.
      if (this.disposed || this.wanted.font !== settings.font) return;
      term.options.fontFamily = fontStack(settings.font);
      this.drawn({ ...this.wanted, font: settings.font });
    }
    this.fit();
  }

  /** Notes what the xterm draws with; on the element too, for the browser tests. */
  private drawn(settings: TerminalSettings): void {
    this.applied = settings;
    this.element.dataset.font = settings.font;
    this.element.dataset.theme = settings.theme;
    this.element.dataset.fontSize = String(settings.fontSize);
  }

  /**
   * The whole pane in the theme's background: the gaps around the cell grid
   * (terminal/geometry.ts) are the terminal's own colour, never the panel's.
   */
  private paint(theme: ITheme | undefined): void {
    this.element.style.backgroundColor = theme?.background ?? "";
  }

  /**
   * Fits rows and columns to the element's size (after a move or a resize),
   * the grid centred with even gaps on all sides (`paneGeometry`). The left
   * and top gaps are the element's padding; the right and bottom ones are
   * what is left. xterm's scrollbar, an overlay, lies along the right edge.
   */
  fit(): void {
    const term = this.term;
    // A fit scheduled by `place` may come after the tab closed.
    if (!term || this.disposed || this.element.clientWidth === 0) return;
    const cell = cellOf(term);
    if (!cell) return;
    const geometry = paneGeometry(
      { width: this.element.clientWidth, height: this.element.clientHeight },
      cell,
    );
    if (!geometry) return;
    this.element.style.padding = `${geometry.top}px 0 0 ${geometry.left}px`;
    if (term.cols === geometry.cols && term.rows === geometry.rows) return;
    try {
      // As xterm's fit addon does before a resize: the next frame draws every row anew.
      coreOf(term)?._renderService?.clear?.();
      term.resize(geometry.cols, geometry.rows);
    } catch {
      // Not laid out yet; the next resize fits it.
    }
  }

  /** Shows the terminal in `pane`, or parks it when null. */
  place(pane: HTMLElement | null): void {
    (pane ?? parking()).append(this.element);
    requestAnimationFrame(() => this.fit());
  }

  /** Text on screen, for tests and screen readers' "copy all". */
  get text(): string {
    const buffer = this.term?.buffer.active;
    if (!buffer) return "";
    const lines: string[] = [];
    for (let i = 0; i < buffer.length; i++)
      lines.push(buffer.getLine(i)?.translateToString(true) ?? "");
    return lines.join("\n");
  }

  /**
   * The screen and up to `scrollback` lines above it, as text, for the
   * assistant's screen context (taken when the person sends a message).
   */
  screenLines(scrollback: number): { lines: string[]; rows: number } {
    const term = this.term;
    const buffer = term?.buffer.active;
    if (!term || !buffer) return { lines: [], rows: 0 };
    const end = buffer.baseY + term.rows;
    const lines: string[] = [];
    for (let i = Math.max(0, end - term.rows - scrollback); i < end; i++)
      lines.push(buffer.getLine(i)?.translateToString(true) ?? "");
    return { lines, rows: term.rows };
  }

  dispose(): void {
    this.disposed = true;
    endSocket(this.socket);
    this.socket = null;
    this.stopTheme?.();
    this.term?.dispose();
    this.element.remove();
  }
}

const safeJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const connections = new Map<string, TerminalConnection>();

export const connectionOf = (id: string): TerminalConnection | undefined => connections.get(id);

export interface OpenRequest {
  projectId: string;
  server: { id: string; name: string; osFlavor: string | null; osVersion: string | null };
  unixUser: string;
  auth: TerminalAuth;
  password?: string;
}

/** Opens a new tab and connects it. */
export function openTerminal(request: OpenRequest): string {
  const id = crypto.randomUUID();
  terminals.add({
    id,
    kind: "ssh",
    projectId: request.projectId,
    serverId: request.server.id,
    serverName: request.server.name,
    osFlavor: request.server.osFlavor,
    osVersion: request.server.osVersion,
    unixUser: request.unixUser,
    auth: request.auth,
    status: "connecting",
    failure: null,
    offeredHostKey: null,
    problem: null,
    hostKeyFingerprint: null,
    sessionId: null,
    unseen: false,
  });
  const connection = new TerminalConnection(id);
  connections.set(id, connection);
  void connection.connect(request.password ? { password: request.password } : {});
  return id;
}

/**
 * A terminal tab rebuilt after a reload (`restore.ts`): its xterm, and at
 * once back to its kept shell (the resume token) or a new session, unless it
 * waits for its password.
 */
export function resumeTerminal(id: string): void {
  if (connections.has(id)) return;
  const connection = new TerminalConnection(id);
  connections.set(id, connection);
  const tab = terminals.tab(id);
  if (tab?.status !== "connecting") return;
  void connection.connect(tab.resumeToken ? { resumeToken: tab.resumeToken } : {});
}

/** Closing a tab ends its SSH session, its remote desktop or its console. */
export function closeTerminal(id: string): void {
  connections.get(id)?.dispose();
  connections.delete(id);
  disposeRdp(id);
  disposeVnc(id);
  terminals.remove(id);
}

// Font, size and theme apply live to every open terminal.
terminalSettings.subscribe(() => {
  const settings = terminalSettings.snapshot();
  for (const connection of connections.values()) void connection.apply(settings);
});
